import React, {useState, useEffect} from "react";
import axiosInstance from "../../AxiosInstance";
import { useTranslation } from 'react-i18next';

const inputStyle = {
    width: '100%', padding: '6px 10px', borderRadius: '6px',
    border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none',
    color: '#334155', boxSizing: 'border-box', textAlign: 'center',
};

export default function ButtonWidget({name, data}) {
    const { t } = useTranslation();
    const [state, setState] = useState(null); // null | 'loading' | 'success' | 'error' | 'blocked'
    const [sliderValue, setSliderValue] = useState(() => {
        if (data?.last_value !== null && data?.last_value !== undefined && data?.button_type === 'slider') {
            const parsed = parseFloat(data.last_value);
            if (!isNaN(parsed)) return parsed;
        }
        return data?.slider_min ?? 0;
    });
    const [toggleOn, setToggleOn] = useState(() => {
        if (data?.last_value !== null && data?.last_value !== undefined && data?.button_type === 'toggle') {
            const v = String(data.last_value);
            return v === '1' || v === 'true' || v === 'True';
        }
        return false;
    });
    const [textValue, setTextValue] = useState(() => {
        if (data?.last_value !== null && data?.last_value !== undefined && data?.button_type === 'text') {
            return data.last_value;
        }
        return '';
    });

    const isSlider = data?.button_type === 'slider';
    const isToggle = data?.button_type === 'toggle';
    const isText = data?.button_type === 'text';
    const sliderMin = data?.slider_min !== undefined && data?.slider_min !== null ? data.slider_min : 0;
    const sliderMax = data?.slider_max !== undefined && data?.slider_max !== null ? data.slider_max : 999999;

    // Initialize state from linked variable on mount
    useEffect(() => {
        if (!data?.linked_variable || !data?.device) return;
        axiosInstance.get(`variable_data/?device_id=${data.device}`).then(res => {
            const vds = Array.isArray(res.data) ? res.data : (res.data?.results || []);
            const vd = vds.find(v => (v.variable?.id ?? v.variable) === data.linked_variable);
            if (!vd) return;
            if (isToggle) {
                const raw = String(vd.value);
                setToggleOn(raw === '1' || raw === 'true' || raw === 'True');
            } else if (isSlider) {
                const parsed = parseFloat(vd.value);
                if (!isNaN(parsed)) setSliderValue(parsed);
            }
        }).catch(() => {});
    }, [data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const patchLastValue = (val) => {
        if (!data?.id) return;
        axiosInstance.patch(`device_action_button/${data.id}/`, {last_value: String(val)}).catch(() => {});
    };

    const triggerAction = async (valueToSend) => {
        if (!data?.id || state === 'loading' || state === 'blocked') return;

        setState('loading');
        const payload = {};

        let nextToggle = null;
        if (isToggle) {
            nextToggle = !toggleOn;
            payload.toggle_value = nextToggle;
        } else if (isSlider && valueToSend !== undefined) {
            payload.slider_value = valueToSend;
        } else if (isText) {
            payload.text_value = valueToSend !== undefined ? valueToSend : textValue;
        }

        // Optimistic toggle update
        if (isToggle && nextToggle !== null) setToggleOn(nextToggle);

        try {
            let linkedVdId = null;
            let originalValue = null;
            if (data.linked_variable && data.device) {
                try {
                    const res = await axiosInstance.get(`variable_data/?device_id=${data.device}`);
                    const vds = Array.isArray(res.data) ? res.data : (res.data?.results || []);
                    const vd = vds.find(v => (v.variable?.id ?? v.variable) === data.linked_variable);
                    if (vd) { linkedVdId = vd.id; originalValue = vd.value; }
                } catch (_) {}
            }

            await axiosInstance.post(`device_action_button/${data.id}/trigger/`, payload);
            setState('success');

            // Persist last_value
            if (isToggle) patchLastValue(nextToggle ? '1' : '0');
            else if (isSlider && valueToSend !== undefined) patchLastValue(valueToSend);
            else if (isText) patchLastValue(valueToSend !== undefined ? valueToSend : textValue);

            if (linkedVdId !== null) {
                setState('blocked');
                const timeoutMs = (data.timeout_seconds || 15) * 1000;
                const deadline = Date.now() + timeoutMs;
                let changed = false;
                let finalVd = null;
                while (Date.now() < deadline && !changed) {
                    await new Promise(r => setTimeout(r, 1500));
                    try {
                        const res = await axiosInstance.get(`variable_data/?device_id=${data.device}`);
                        const vds = Array.isArray(res.data) ? res.data : (res.data?.results || []);
                        const vd = vds.find(v => v.id === linkedVdId);
                        if (vd && String(vd.value) !== String(originalValue)) { changed = true; finalVd = vd; }
                    } catch (_) {}
                }
                // Sync toggle with real variable value after polling
                if (finalVd && isToggle) {
                    const raw = String(finalVd.value);
                    setToggleOn(raw === '1' || raw === 'true' || raw === 'True');
                }
                setState(null);
            } else {
                setTimeout(() => setState(null), 2000);
            }
        } catch (_) {
            setState('error');
            // Revert optimistic toggle
            if (isToggle) setToggleOn(prev => !prev);
            setTimeout(() => setState(null), 2000);
        }
    };

    const isBlocked = state === 'loading' || state === 'blocked';
    const displayName = name || data?.name || t('common.trigger');

    // ── Toggle ──────────────────────────────────────────────────────────────
    if (isToggle) {
        const trackColor = toggleOn ? '#4F46E5' : '#CBD5E1';
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                height: '100%', padding: '8px', boxSizing: 'border-box', gap: '6px',
                opacity: isBlocked ? 0.65 : 1,
                backgroundColor: '#fff', borderRadius: '6px', boxShadow: '#b4b6c0 0 0 2px',
            }}>
                <div style={{fontSize: '12px', fontWeight: '600', color: '#334155', textAlign: 'center'}}>
                    {displayName}
                </div>
                <div
                    onClick={() => { if (!isBlocked) triggerAction(); }}
                    style={{
                        width: '44px', height: '24px', borderRadius: '12px',
                        backgroundColor: trackColor,
                        position: 'relative', cursor: isBlocked ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.25s', flexShrink: 0,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                    }}
                >
                    <div style={{
                        position: 'absolute', top: '3px',
                        left: toggleOn ? '23px' : '3px',
                        width: '18px', height: '18px', borderRadius: '50%',
                        backgroundColor: '#fff',
                        transition: 'left 0.25s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                    }}/>
                </div>
                <div style={{fontSize: '11px', fontWeight: '500', color: toggleOn ? '#4F46E5' : '#94A3B8'}}>
                    {toggleOn ? t('variables.toggleOn', 'On') : t('variables.toggleOff', 'Off')}
                </div>
                {state === 'blocked' && (
                    <div style={{fontSize: '10px', color: '#4F46E5'}}>{t('common.waiting', 'Esperando...')}</div>
                )}
                {state === 'error' && (
                    <div style={{fontSize: '10px', color: '#EF4444'}}>{t('common.failed')}</div>
                )}
            </div>
        );
    }

    // ── Text Input ──────────────────────────────────────────────────────────
    if (isText) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center',
                height: '100%', padding: '12px 16px', boxSizing: 'border-box', gap: '6px',
                opacity: isBlocked ? 0.65 : 1,
                backgroundColor: '#fff', borderRadius: '6px', boxShadow: '#b4b6c0 0 0 2px',
            }}>
                <div style={{fontSize: '12px', fontWeight: '600', color: '#334155', textAlign: 'center', marginBottom: '2px'}}>
                    {displayName}
                </div>
                <div style={{display: 'flex', gap: '6px'}}>
                    <input
                        type="text"
                        value={textValue}
                        disabled={isBlocked}
                        onChange={e => setTextValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !isBlocked) triggerAction(textValue); }}
                        placeholder={t('variables.textPlaceholder', 'Escribe el valor...')}
                        style={{
                            ...inputStyle,
                            flex: 1, textAlign: 'left',
                            cursor: isBlocked ? 'not-allowed' : 'text',
                        }}
                    />
                    <button
                        onClick={() => { if (!isBlocked) triggerAction(textValue); }}
                        disabled={isBlocked}
                        style={{
                            background: state === 'success' ? '#22C55E' : state === 'error' ? '#EF4444' : '#4F46E5',
                            color: '#fff', border: 'none', borderRadius: '6px',
                            padding: '6px 12px', cursor: isBlocked ? 'not-allowed' : 'pointer',
                            fontSize: '13px', fontWeight: '600', transition: 'background 0.2s', flexShrink: 0,
                        }}
                    >
                        {state === 'loading' ? '...' : state === 'success' ? '✓' : state === 'error' ? '✗' : '↑'}
                    </button>
                </div>
                {state === 'blocked' && (
                    <div style={{fontSize: '11px', color: '#4F46E5', textAlign: 'center'}}>{t('common.waiting', 'Esperando...')}</div>
                )}
            </div>
        );
    }

    // ── Slider ──────────────────────────────────────────────────────────────
    if (isSlider) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center',
                height: '100%', padding: '12px 16px', boxSizing: 'border-box', gap: '6px',
                opacity: isBlocked ? 0.65 : 1,
                backgroundColor: '#fff', borderRadius: '6px', boxShadow: '#b4b6c0 0 0 2px',
            }}>
                <div style={{fontSize: '12px', fontWeight: '600', color: '#334155', textAlign: 'center', marginBottom: '2px'}}>
                    {displayName}
                </div>
                <input
                    type="range"
                    min={sliderMin} max={sliderMax} step={1}
                    value={sliderValue}
                    disabled={isBlocked}
                    onChange={e => setSliderValue(parseFloat(e.target.value))}
                    onMouseUp={e => { if (!isBlocked) triggerAction(parseFloat(e.target.value)); }}
                    onTouchEnd={() => { if (!isBlocked) triggerAction(sliderValue); }}
                    style={{width: '100%', accentColor: '#4F46E5', cursor: isBlocked ? 'not-allowed' : 'pointer'}}
                />
                <input
                    type="number"
                    min={sliderMin} max={sliderMax !== 999999 ? sliderMax : undefined}
                    value={sliderValue}
                    disabled={isBlocked}
                    onChange={e => setSliderValue(parseFloat(e.target.value) || 0)}
                    onKeyDown={e => { if (e.key === 'Enter' && !isBlocked) triggerAction(sliderValue); }}
                    onBlur={e => {
                        const val = parseFloat(e.target.value) || 0;
                        if (!isBlocked) triggerAction(val);
                    }}
                    style={{...inputStyle, cursor: isBlocked ? 'not-allowed' : 'auto'}}
                />
                {state === 'blocked' && (
                    <div style={{fontSize: '11px', color: '#4F46E5', textAlign: 'center'}}>{t('common.waiting', 'Esperando...')}</div>
                )}
                {state === 'success' && (
                    <div style={{fontSize: '11px', color: '#22C55E', textAlign: 'center'}}>{t('common.done')}</div>
                )}
                {state === 'error' && (
                    <div style={{fontSize: '11px', color: '#EF4444', textAlign: 'center'}}>{t('common.failed')}</div>
                )}
            </div>
        );
    }

    // ── Button ──────────────────────────────────────────────────────────────
    const bgColor = state === 'success' ? '#22C55E' : state === 'error' ? '#EF4444' : state === 'blocked' ? '#6366F1' : '#4F46E5';
    const label = state === 'success' ? t('common.done') : state === 'error' ? t('common.failed') : state === 'blocked' ? '...' : displayName;

    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', padding: '8px', boxSizing: 'border-box',
            backgroundColor: '#fff', borderRadius: '6px', boxShadow: '#b4b6c0 0 0 2px',
        }}>
            <button
                onClick={() => triggerAction()}
                disabled={isBlocked}
                style={{
                    backgroundColor: bgColor,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isBlocked ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.3s',
                    minWidth: '120px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
            >
                {state === 'loading' ? '...' : label}
            </button>
        </div>
    );
}
